import type { ReactElement } from "react";
import type {
  MoneyUnitStyle,
  MoneyValueProps,
} from "@/modules/dashboard/types/dashboard-analytics.types";

/**
 * Unit typography per number tier.
 *
 * The rule the approved design follows: the unit is roughly half the number's
 * size, one weight lighter, muted, and separated by margin rather than by a
 * space character inside the string. Sizes are Tailwind arbitrary values to
 * match the numbers they sit beside; the colour is the muted token, never a hex.
 */
export const HEADLINE_UNIT_STYLE: MoneyUnitStyle = {
  className: "text-[13px] font-semibold text-[color:var(--text-muted)]",
  gap: "5px",
};

export const STAT_UNIT_STYLE: MoneyUnitStyle = {
  className: "text-[11.5px] font-semibold text-[color:var(--text-muted)]",
  gap: "4px",
};

export const SIGNAL_UNIT_STYLE: MoneyUnitStyle = {
  className: "text-[12px] font-semibold text-[color:var(--text-muted)]",
  gap: "5px",
};

/**
 * A money figure with its currency symbol rendered as a SUBORDINATE unit.
 *
 * Printed as one run at one size ("٩٩٠ ج.م" at 24px/700) the symbol reads as
 * part of the figure — three more glyphs of the number. Splitting it into its
 * own element at half the size, muted, is what makes it read as a unit.
 *
 * Deliberately dumb: it receives two already-formatted strings and the classes
 * to wear. All formatting — digits, grouping, the house symbol, and which side
 * CLDR puts it on — belongs to `formatCurrencyParts`.
 *
 * The `<bdi>` wrapper stays: it isolates the figure so a mixed Arabic/Latin
 * line cannot reorder it. The gap is a LOGICAL margin (`margin-inline-*`) on
 * the side facing the figure, so it flips with direction instead of being
 * pinned to the left.
 */
export function MoneyValue({
  amount,
  unit,
  unitFirst,
  unitStyle,
}: MoneyValueProps): ReactElement {
  const symbol = (
    <small
      className={unitStyle.className}
      style={
        unitFirst
          ? { marginInlineEnd: unitStyle.gap }
          : { marginInlineStart: unitStyle.gap }
      }
    >
      {unit}
    </small>
  );

  return (
    <bdi>
      {unitFirst ? symbol : null}
      {amount}
      {unitFirst ? null : symbol}
    </bdi>
  );
}
